/**
 * InlineDateEditor — shared inline send date/time/timezone editor used by both
 * the Queue tab (ChainCard) and the Scheduled Sends tab to reschedule a campaign.
 * The parent owns the edit state; this is purely presentational.
 */

interface InlineDateEditorProps {
  editDate: string;
  editTime: string;
  editTimezone: string;
  editDateSaving: boolean;
  editDateError: string | null;
  onDateChange: (v: string) => void;
  onTimeChange: (v: string) => void;
  onTimezoneChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

export default function InlineDateEditor({
  editDate,
  editTime,
  editTimezone,
  editDateSaving,
  editDateError,
  onDateChange,
  onTimeChange,
  onTimezoneChange,
  onSave,
  onCancel,
}: InlineDateEditorProps) {
  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-full sm:w-40">
          <label className="block text-xs font-medium text-warm-600 mb-1">Date</label>
          <input
            type="date"
            value={editDate}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => onDateChange(e.target.value)}
            className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div className="w-full sm:w-28">
          <label className="block text-xs font-medium text-warm-600 mb-1">Time</label>
          <input
            type="time"
            value={editTime}
            onChange={(e) => onTimeChange(e.target.value)}
            className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div className="w-full sm:w-40">
          <label className="block text-xs font-medium text-warm-600 mb-1">Timezone</label>
          <select
            value={editTimezone}
            onChange={(e) => onTimezoneChange(e.target.value)}
            className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="America/New_York">Eastern</option>
            <option value="America/Chicago">Central</option>
            <option value="America/Denver">Mountain</option>
            <option value="America/Los_Angeles">Pacific</option>
          </select>
        </div>
      </div>
      {editDateError && <p className="text-danger-600 text-xs">{editDateError}</p>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={!editDate || editDateSaving}
          className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {editDateSaving ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-xs text-warm-600 hover:text-warm-900 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
